import {
  schoolDeviceEnrollmentResponseSchema,
  schoolDeviceCommandResponseSchema,
  schoolDevicesResponseSchema,
  schoolLearningSettingsResponseSchema,
  schoolInvitationAcceptResponseSchema,
  schoolInvitationCreateResponseSchema,
  schoolInvitationDetailsResponseSchema,
  schoolMemberMutationResponseSchema,
  schoolMembersResponseSchema,
  schoolProfileResponseSchema,
  schoolGuardianCodeUpdateResponseSchema,
  type SchoolDeviceEnrollmentCreateRequest,
  type SchoolDeviceCommandCreateRequest,
  type SchoolLearningSettingsUpdateRequest,
  type SchoolInvitationAcceptRequest,
  type SchoolInvitationCreateRequest,
  type SchoolMembersQuery,
  type SchoolMemberRoleUpdateRequest,
  type SchoolProfileUpdateRequest,
} from "@kindtutor/api-contract";
import {
  localizedApiErrorSchema as apiErrorSchema,
  throwApiError,
} from "@kindtutor/auth-client";
import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchSchoolLearningSettings(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/learning-settings`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać zasad nauki.");
  return schoolLearningSettingsResponseSchema.parse(await response.json());
}

export async function updateSchoolLearningSettings(
  schoolId: string,
  input: SchoolLearningSettingsUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/learning-settings`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać zasad nauki.",
    );
  }
  return schoolLearningSettingsResponseSchema.parse(body);
}

export async function fetchSchoolDevices(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/devices`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać stanowisk.");
  return schoolDevicesResponseSchema.parse(await response.json());
}

export async function createSchoolDeviceCommand(
  schoolId: string,
  deviceId: string,
  input: SchoolDeviceCommandCreateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/devices/${encodeURIComponent(deviceId)}/commands`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się wysłać polecenia.",
    );
  }
  return schoolDeviceCommandResponseSchema.parse(body);
}

export async function resetSchoolDevicePairing(
  schoolId: string,
  deviceId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/devices/${encodeURIComponent(deviceId)}/reset-pairing`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zresetować parowania stanowiska.",
    );
  }
  return schoolDeviceEnrollmentResponseSchema.parse(body);
}

export async function createSchoolDeviceEnrollment(
  schoolId: string,
  input: SchoolDeviceEnrollmentCreateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/device-enrollments`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć kodu parowania.",
    );
  }
  return schoolDeviceEnrollmentResponseSchema.parse(body);
}

async function parseSchoolMutationError(
  response: Response,
  fallback: string,
): Promise<never> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const error = apiErrorSchema.safeParse(body);
  throw new Error(error.success ? error.data.error.message : fallback);
}

export async function fetchSchoolMembers(
  schoolId: string,
  input: Partial<SchoolMembersQuery> = {},
) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.role) query.set("role", input.role);
  if (input.status) query.set("status", input.status);
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/members${suffix}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się wczytać osób i zaproszeń.",
    );
  }
  return schoolMembersResponseSchema.parse(await response.json());
}

export async function createSchoolInvitation(
  schoolId: string,
  input: SchoolInvitationCreateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/invitations`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się wysłać zaproszenia.",
    );
  }
  return schoolInvitationCreateResponseSchema.parse(await response.json());
}

export async function updateSchoolMemberRole(
  schoolId: string,
  membershipId: string,
  input: SchoolMemberRoleUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/members/${encodeURIComponent(membershipId)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    return parseSchoolMutationError(response, "Nie udało się zmienić roli.");
  }
  return schoolMemberMutationResponseSchema.parse(await response.json());
}

export async function revokeSchoolMember(
  schoolId: string,
  membershipId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/members/${encodeURIComponent(membershipId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!response.ok) {
    return parseSchoolMutationError(response, "Nie udało się odebrać dostępu.");
  }
}

export async function cancelSchoolInvitation(
  schoolId: string,
  invitationId: string,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się anulować zaproszenia.",
    );
  }
}

export async function fetchSchoolInvitation(token: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/invitations/${encodeURIComponent(token)}`,
    { headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Zaproszenie wygasło albo zostało anulowane.",
    );
  }
  return schoolInvitationDetailsResponseSchema.parse(await response.json());
}

export async function acceptSchoolInvitation(
  input: SchoolInvitationAcceptRequest,
) {
  const response = await apiFetch(`${apiBaseURL}/v1/invitations/accept`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się przyjąć zaproszenia.",
    );
  }
  return schoolInvitationAcceptResponseSchema.parse(await response.json());
}

export async function fetchSchoolProfile(schoolId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/profile`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się wczytać profilu szkoły.",
    );
  }
  return schoolProfileResponseSchema.parse(await response.json());
}

export async function updateSchoolProfile(
  schoolId: string,
  input: SchoolProfileUpdateRequest,
) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/profile`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się zapisać profilu szkoły.",
    );
  }
  return schoolProfileResponseSchema.parse(await response.json());
}

export async function updateSchoolGuardianCode(schoolId: string, code: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/schools/${encodeURIComponent(schoolId)}/guardian-code`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    },
  );
  if (!response.ok) {
    return parseSchoolMutationError(
      response,
      "Nie udało się zmienić kodu opiekuna.",
    );
  }
  return schoolGuardianCodeUpdateResponseSchema.parse(await response.json());
}
